import {
  DataTable,
  DataTableColDef,
  DataTableColumnHeader,
} from "@/lib/Components/DataTable/DataTable";
import { TimeSinceNowCell } from "@/lib/Components/DataTable/TimeSinceNowCell";
import { useRelocationPageActions } from "@/app/Settings/Admin/RelocationPages/hooks/useRelocationPageActions";
import { CellContext } from "@/lib/Components/DataTable/types";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { PencilIcon } from "@heroicons/react/24/outline";
import { UploadMediaDialog } from "@/app/Media/Components/UploadMediaDialog";
import {
  MediaCollection,
  ModelType,
  QueryRelocationPagesOrderByColumn,
  SortOrder,
} from "@/gql/graphql";
import { ImoovaImage } from "@/app/Media/Components/Image";
import { ImageIcon } from "lucide-react";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/20/solid";
import {
  RelocationPageListItem,
  relocationPageListQuery,
} from "@/app/Settings/Admin/RelocationPages/GraphQL/relocationPageListQuery";
import { useNavigate } from "@tanstack/react-router";

export function RelocationPageTable() {
  const getActions = useRelocationPageActions();

  const columns: DataTableColDef<RelocationPageListItem, any, any>[] = [
    {
      id: "title",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Title" />
      ),
      accessorFn: (row) => row,
      cell: RelocationPageTitleCell,
      enableSorting: true,
    },
    {
      id: "image",
      header: "Image",
      accessorFn: (row) => row,
      cell: RelocationPageImageCell,
    },
    {
      id: "slug",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Slug" />
      ),
      accessorFn: (row) => row.slug,
      cell: ({ getValue }) => {
        const slug = getValue();

        return (
          <a
            className="underline"
            href={new URL(
              `/routes/${slug}`,
              import.meta.env.VITE_APP_URL,
            ).toString()}
            target="_blank"
            rel="noreferrer"
          >
            {slug} <ArrowTopRightOnSquareIcon className="inline size-4" />
          </a>
        );
      },
    },
    {
      id: "created",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Created" />
      ),
      accessorFn: (row) => row.created_at,
      cell: TimeSinceNowCell,
    },
  ];

  return (
    <DataTable
      getActions={getActions}
      columns={columns}
      document={relocationPageListQuery}
      getQueryVariables={({ pagination, sorting, search }) => {
        const orderBy = sorting.map((sort) => {
          let column: QueryRelocationPagesOrderByColumn;
          switch (sort.id) {
            case "title":
              column = QueryRelocationPagesOrderByColumn.Title;
              break;
            case "slug":
              column = QueryRelocationPagesOrderByColumn.Slug;
              break;
            case "created":
              column = QueryRelocationPagesOrderByColumn.CreatedAt;
              break;
            default:
              column = QueryRelocationPagesOrderByColumn.CreatedAt;
          }
          return {
            column,
            order: sort.desc ? SortOrder.Desc : SortOrder.Asc,
          };
        });

        return {
          first: pagination.pageSize,
          page: pagination.pageIndex,
          orderBy: orderBy.length > 0 ? orderBy : undefined,
          search,
        };
      }}
      accessor={(data) => data.relocationPages}
      id="relocation-pages"
      initialSorting={[{ id: "created", desc: true }]}
    />
  );
}

function RelocationPageTitleCell({
  row,
}: CellContext<RelocationPageListItem, any>) {
  const relocationPage = row.original;
  const navigate = useNavigate();

  return (
    <button
      onClick={() => {
        navigate({
          to: ".",
          search: (prev: any) => ({
            ...prev,
            relocationPageId: relocationPage.id,
          }),
        });
      }}
      className="text-left font-medium text-indigo-600 hover:text-indigo-800 hover:underline"
    >
      {relocationPage.title}
    </button>
  );
}

function RelocationPageImageCell({
  row,
}: CellContext<RelocationPageListItem, any>) {
  const relocationPage = row.original;
  const { open } = useDialog(UploadMediaDialog);

  return (
    <button
      onClick={() => {
        open({
          objectId: relocationPage.id,
          objectType: ModelType.RelocationPage,
          collection: MediaCollection.Images,
        });
      }}
      className="relative"
    >
      {relocationPage.image ? (
        <ImoovaImage
          alt={relocationPage.title}
          media={relocationPage.image}
          className="size-10"
        />
      ) : (
        <ImageIcon className="size-10" />
      )}
      <div className="absolute inset-0 flex items-center justify-center overflow-hidden rounded-full bg-gray-700/50 opacity-0 hover:opacity-100">
        <PencilIcon className="size-4 text-white" />
      </div>
    </button>
  );
}
